/* Recordings: what is in the database, and putting one there.

   The audio of a lesson is the one part of the material that git does not hold,
   so this page is the only place it can be looked at. It says the size and the
   digest of every recording for the same reason: those two answer "is this the
   one I uploaded" without downloading it back.

   Uploading and replacing are delegable; deleting is not, and the button is
   simply absent for an account that may not. The server refuses it either way —
   what a page draws is a convenience, never a decision.

   The copy on disk is the other half of this and is not here: `scripts/media.mjs
   pull` is what keeps a reset database from taking every recording with it, and
   the note at the top of this panel says so, because a page that makes deleting
   easy owes the reader the sentence about backups.

   OWNED BY ITEM 7 OF PLAN.md. */

import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, can, type MediaRow, type Me } from "../../lib/api";
import { useI18n } from "../../lib/i18n";

function size(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} kB`;
}

export function Media({ me }: { me: Me }) {
  const { ui } = useI18n();
  const [rows, setRows] = useState<MediaRow[]>([]);
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; bad?: boolean } | null>(null);
  const picked = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    void api<MediaRow[]>("/media").then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  async function upload() {
    const file = picked.current?.files?.[0];
    if (!file) return setMsg({ text: ui("media.pickFirst"), bad: true });
    if (!/^[a-z0-9-]+$/.test(slug)) return setMsg({ text: ui("media.badSlug"), bad: true });

    setBusy(true);
    setMsg(null);
    const body = new FormData();
    body.append("slug", slug);
    body.append("title", title);
    body.append("file", file);
    try {
      /* No Content-Type of our own: the boundary is the browser's to write, and
         setting the header by hand is how a multipart body arrives unparseable. */
      const res = await fetch("/api/media", { method: "POST", body, credentials: "same-origin" });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new ApiError(res.status, detail.detail ?? ui("media.failed"));
      }
      setMsg({ text: ui("media.saved") });
      setSlug("");
      setTitle("");
      if (picked.current) picked.current.value = "";
      load();
    } catch (err) {
      setMsg({ text: err instanceof ApiError ? err.message : ui("media.failed"), bad: true });
    } finally {
      setBusy(false);
    }
  }

  async function remove(row: MediaRow) {
    if (!window.confirm(ui("media.confirmRemove").replace("{slug}", row.slug))) return;
    try {
      await api<void>(`/media/${row.slug}`, { method: "DELETE" });
      setMsg({ text: ui("media.removed").replace("{slug}", row.slug) });
      load();
    } catch (err) {
      setMsg({ text: err instanceof ApiError ? err.message : ui("media.failed"), bad: true });
    }
  }

  return (
    <section className="adm-panel">
      <p className="adm-sub">{ui("media.lead")}</p>
      <p className="adm-banner">
        {ui("media.backup")} <code>node scripts/media.mjs pull</code>
      </p>

      <div className="adm-mint">
        <label className="adm-field">
          {ui("media.slug")}
          <input type="text" value={slug} spellCheck={false}
                 onChange={(e) => setSlug(e.target.value.trim())} />
        </label>
        <label className="adm-field">
          {ui("media.title")}
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="adm-field">
          {ui("media.file")}
          <input type="file" accept="audio/*" ref={picked} />
        </label>
        <button type="button" disabled={busy} onClick={() => void upload()}>
          {ui("media.upload")}
        </button>
      </div>

      {msg ? <p className={"adm-msg" + (msg.bad ? " bad" : "")}>{msg.text}</p> : null}

      {rows.length ? (
        <table>
          <thead>
            <tr>
              <th>{ui("media.slug")}</th>
              <th>{ui("media.title")}</th>
              <th>{ui("media.size")}</th>
              <th>{ui("media.digest")}</th>
              <th>{ui("media.changed")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.slug}>
                <td><code>{row.slug}</code></td>
                <td>{row.title}</td>
                <td>{size(row.size)}</td>
                <td><code>{row.sha256.slice(0, 8)}</code></td>
                <td>{row.updated_at.slice(0, 10)} {row.updated_by ? `· ${row.updated_by}` : ""}</td>
                <td>
                  {can(me, "media.delete") ? (
                    <button type="button" className="danger" onClick={() => void remove(row)}>
                      {ui("media.remove")}
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <p className="adm-empty">{ui("media.none")}</p>}
    </section>
  );
}
